import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const RegistrationForm = ({ onRegister }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
    agreeToTerms: false,
    githubIntegration: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [validationStatus, setValidationStatus] = useState({
    username: null,
    email: null,
    password: {
      minLength: false,
      hasSpecialChar: false,
      hasNumber: false,
      hasUppercase: false
    }
  });

  const roleOptions = [
    { value: 'junior-developer', label: 'Junior Developer' },
    { value: 'qa-engineer', label: 'QA Engineer' },
    { value: 'student', label: 'Student' },
    { value: 'other', label: 'Other' }
  ];

  const validatePassword = (password) => {
    return {
      minLength: password?.length >= 8,
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/?.test(password),
      hasNumber: /\d/?.test(password),
      hasUppercase: /[A-Z]/?.test(password)
    };
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear errors when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Real-time validation
    if (field === 'username') {
      // Simulate username availability check
      setTimeout(() => {
        const isAvailable = !['admin', 'test', 'user']?.includes(value?.toLowerCase());
        setValidationStatus(prev => ({
          ...prev,
          username: value?.length >= 3 ? (isAvailable ? 'available' : 'taken') : null
        }));
      }, 500);
    }

    if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setTimeout(() => {
        const isValid = emailRegex?.test(value);
        setValidationStatus(prev => ({
          ...prev,
          email: value ? (isValid ? 'valid' : 'invalid') : null
        }));
      }, 300);
    }

    if (field === 'password') {
      setValidationStatus(prev => ({
        ...prev,
        password: validatePassword(value)
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.username?.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData?.username?.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else {
      const passwordValidation = validatePassword(formData?.password);
      if (!Object.values(passwordValidation)?.every(Boolean)) {
        newErrors.password = 'Password does not meet all requirements';
      }
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.role) {
      newErrors.role = 'Please select your role';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms of service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock successful registration
      const newUser = {
        id: Date.now(),
        username: formData?.username,
        email: formData?.email,
        role: formData?.role,
        level: 1,
        joinedDate: new Date()?.toISOString(),
        githubConnected: formData?.githubIntegration
      };

      onRegister(newUser);
      navigate('/mission-map-dashboard');
    } catch (error) {
      setErrors({ submit: 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const getValidationIcon = (status) => {
    if (status === 'available' || status === 'valid') {
      return <Icon name="CheckCircle" size={16} color="var(--color-success)" />;
    } else if (status === 'taken' || status === 'invalid') {
      return <Icon name="XCircle" size={16} color="var(--color-error)" />;
    }
    return null;
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Username Field */}
        <div className="relative">
          <Input
            label="Username"
            type="text"
            placeholder="Choose a unique username"
            value={formData?.username}
            onChange={(e) => handleInputChange('username', e?.target?.value)}
            error={errors?.username}
            required
            className="pr-10"
          />
          {validationStatus?.username && (
            <div className="absolute right-3 top-9">
              {getValidationIcon(validationStatus?.username)}
            </div>
          )}
          {validationStatus?.username === 'taken' && (
            <p className="text-xs text-error mt-1">Username is already taken</p>
          )}
          {validationStatus?.username === 'available' && (
            <p className="text-xs text-success mt-1">Username is available</p>
          )}
        </div>

        {/* Email Field */}
        <div className="relative">
          <Input
            label="Email Address"
            type="email"
            placeholder="your.email@example.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
            className="pr-10"
          />
          {validationStatus?.email && (
            <div className="absolute right-3 top-9">
              {getValidationIcon(validationStatus?.email)}
            </div>
          )}
        </div>

        {/* Password Field */}
        <div>
          <Input
            label="Password"
            type="password"
            placeholder="Create a strong password"
            value={formData?.password}
            onChange={(e) => handleInputChange('password', e?.target?.value)}
            error={errors?.password}
            required
          />
          {formData?.password && (
            <div className="mt-2 space-y-1">
              <div className="flex items-center space-x-2">
                <Icon 
                  name={validationStatus?.password?.minLength ? "CheckCircle" : "Circle"} 
                  size={12} 
                  color={validationStatus?.password?.minLength ? "var(--color-success)" : "var(--color-muted-foreground)"} 
                />
                <span className={`text-xs ${validationStatus?.password?.minLength ? 'text-success' : 'text-muted-foreground'}`}>
                  At least 8 characters
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon 
                  name={validationStatus?.password?.hasUppercase ? "CheckCircle" : "Circle"} 
                  size={12} 
                  color={validationStatus?.password?.hasUppercase ? "var(--color-success)" : "var(--color-muted-foreground)"} 
                />
                <span className={`text-xs ${validationStatus?.password?.hasUppercase ? 'text-success' : 'text-muted-foreground'}`}>
                  One uppercase letter
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon 
                  name={validationStatus?.password?.hasNumber ? "CheckCircle" : "Circle"} 
                  size={12} 
                  color={validationStatus?.password?.hasNumber ? "var(--color-success)" : "var(--color-muted-foreground)"} 
                />
                <span className={`text-xs ${validationStatus?.password?.hasNumber ? 'text-success' : 'text-muted-foreground'}`}>
                  One number
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon 
                  name={validationStatus?.password?.hasSpecialChar ? "CheckCircle" : "Circle"} 
                  size={12} 
                  color={validationStatus?.password?.hasSpecialChar ? "var(--color-success)" : "var(--color-muted-foreground)"} 
                />
                <span className={`text-xs ${validationStatus?.password?.hasSpecialChar ? 'text-success' : 'text-muted-foreground'}`}>
                  One special character
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Confirm Password Field */}
        <Input
          label="Confirm Password"
          type="password"
          placeholder="Confirm your password"
          value={formData?.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
          error={errors?.confirmPassword}
          required
        />

        {/* Role Selector */}
        <Select
          label="Your Role"
          placeholder="Select your current role"
          options={roleOptions}
          value={formData?.role}
          onChange={(value) => handleInputChange('role', value)}
          error={errors?.role}
          required
        />

        {/* Terms Agreement */}
        <Checkbox
          label="I agree to the Terms of Service and Privacy Policy"
          checked={formData?.agreeToTerms}
          onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
          error={errors?.agreeToTerms}
          required
        />

        {/* GitHub Integration */}
        <Checkbox
          label="Connect with GitHub for enhanced features"
          description="Link your GitHub account for code sharing and portfolio integration"
          checked={formData?.githubIntegration}
          onChange={(e) => handleInputChange('githubIntegration', e?.target?.checked)}
        />

        {/* Submit Button */}
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
          iconName="UserPlus"
          iconPosition="left"
        >
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </Button>

        {errors?.submit && (
          <div className="text-center text-error text-sm mt-2">
            {errors?.submit}
          </div>
        )}

        {/* Login Link */}
        <div className="text-center">
          <span className="text-sm text-muted-foreground">
            Already have an account?{' '}
          </span>
          <button
            type="button"
            onClick={() => navigate('/user-login')}
            className="text-sm text-primary hover:underline focus-ring rounded"
          >
            Sign in here
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;